import * as React from 'react';

export type EditorOption<TSelected> = { key: string; label: string; item: TSelected };

type Args<TItem, TSelected> = {
  value: TSelected[];
  onChange: (value: TSelected[]) => void;
  availableItems: readonly TItem[];
  input: string;
  /** Returns the catalog ID for an available item. */
  getItemId: (item: TItem) => string;
  /** Returns the display name for an available item. */
  getItemName: (item: TItem) => string;
  /** Returns the catalog ID if the selected item is a catalog item, null if user-generated. */
  getSelectedCatalogId: (selected: TSelected) => string | null;
  /** Returns the display name of any selected item (catalog or user-generated). */
  getSelectedName: (selected: TSelected) => string;
  /** Converts an available item to a selected catalog item. */
  makeSelectedFromItem: (item: TItem) => TSelected;
  /** Creates a user-generated selected item from the raw input text. */
  makeUserGenerated: (trimmedInput: string) => TSelected;
  /** Adds an item to the selection, deduplicating as needed. */
  addItem: (value: TSelected[], item: TSelected) => TSelected[];
  /** Sentinel option key for the "create user-generated" option. */
  createOptionKey: string;
};

const normalize = (text: string) => text.trim().toLowerCase();

/**
 * Generic multi-select typeahead hook. Computes the option list (catalog items
 * minus already-selected ones, plus a "create" option when the input has no
 * exact match) and provides add-from-option / add-from-input actions.
 *
 * All adapter arguments (`getItemId`, `makeUserGenerated`, etc.) must be stable
 * references (module-scope constants or memoized) to avoid unnecessary option
 * recomputation.
 */
export const useMultiSelectTypeaheadEditor = <TItem, TSelected>({
  value,
  onChange,
  availableItems,
  input,
  getItemId,
  getItemName,
  getSelectedCatalogId,
  getSelectedName,
  makeSelectedFromItem,
  makeUserGenerated,
  addItem,
  createOptionKey,
}: Args<TItem, TSelected>) => {
  const trimmedInput = input.trim();

  const options = React.useMemo<EditorOption<TSelected>[]>(() => {
    const normalizedInput = normalize(input);
    const selectedCatalogIds = new Set(
      value.flatMap((s) => {
        const id = getSelectedCatalogId(s);
        return id !== null ? [id] : [];
      })
    );

    const catalogOptions: EditorOption<TSelected>[] = availableItems
      .filter((item) => !selectedCatalogIds.has(getItemId(item)))
      .map((item) => ({ key: getItemId(item), label: getItemName(item), item: makeSelectedFromItem(item) }));

    const matchesExistingLabel =
      availableItems.some((item) => normalize(getItemName(item)) === normalizedInput) ||
      value.some((s) => normalize(getSelectedName(s)) === normalizedInput);

    const createOption: EditorOption<TSelected>[] =
      trimmedInput && !matchesExistingLabel
        ? [{ key: createOptionKey, label: trimmedInput, item: makeUserGenerated(trimmedInput) }]
        : [];

    return [...createOption, ...catalogOptions];
  }, [
    availableItems,
    value,
    input,
    trimmedInput,
    getItemId,
    getItemName,
    getSelectedCatalogId,
    getSelectedName,
    makeSelectedFromItem,
    makeUserGenerated,
    createOptionKey,
  ]);

  /** Adds the option's item and returns the full resulting selection, or null if the key doesn't match an option. */
  const addFromOption = (key: string): TSelected[] | null => {
    const selected = options.find((o) => o.key === key);
    if (!selected) return null;
    const next = addItem(value, selected.item);
    onChange(next);
    return next;
  };

  /**
   * Resolves `rawInput` to a catalog item (exact name match) or a user-generated
   * item, adds it, and returns the full resulting selection -- so callers can
   * report what was actually persisted rather than the raw typed text.
   */
  const addFromInput = (rawInput: string): TSelected[] | null => {
    const trimmed = rawInput.trim();
    if (!trimmed) return null;
    const exactMatch = availableItems.find((item) => normalize(getItemName(item)) === normalize(trimmed));
    const next = addItem(value, exactMatch ? makeSelectedFromItem(exactMatch) : makeUserGenerated(trimmed));
    onChange(next);
    return next;
  };

  return { options, trimmedInput, addFromOption, addFromInput };
};
